import { z } from 'zod';

export const UserTypeSchema = z.enum([
  'doctor',
  'pharmacist',
  'nurse_midwife',
  'pharma',
  'medical_place',
  'hospital',
]);

export type UserType = z.infer<typeof UserTypeSchema>;

// Every type an account can have. `partner` is admin-assigned only, so it's
// absent from UserTypeSchema (what users can pick in the profile wizard).
export type AccountUserType = UserType | 'partner';

function isValidDoctorPwz(pwz: string): boolean {
  if (!/^[1-9]\d{6}$/.test(pwz)) return false;
  const digits = pwz.split('').map(Number);
  const [check, ...rest] = digits;
  const sum = rest.reduce((acc, d, i) => acc + d * (i + 1), 0);
  return sum % 11 === check;
}

function isValidPharmacistPwz(pwz: string): boolean {
  if (!/^\d{8}$/.test(pwz)) return false;
  const digits = pwz.split('').map(Number);
  const weights = [3, 1, 3, 1, 3, 1, 3];
  const sum = weights.reduce((acc, w, i) => acc + w * digits[i]!, 0);
  return (10 - (sum % 10)) % 10 === digits[7];
}

// NIPiP PWZ format: 7 digits + letter P (pielęgniarka) or A (położna).
// No checksum — NIPiP explicitly eliminated the check digit for this group.
function isValidNurseMidwifePwz(pwz: string): boolean {
  return /^\d{7}[PA]$/i.test(pwz);
}

export const IndividualProfileSchema = z
  .object({
    userType: z.enum(['doctor', 'pharmacist', 'nurse_midwife']),
    fullName: z.string().min(2, 'Imię i nazwisko jest wymagane'),
    licenseNumber: z
      .string()
      .trim()
      .min(1, 'Numer prawa wykonywania zawodu jest wymagany'),
  })
  .superRefine((data, ctx) => {
    const { userType, licenseNumber } = data;

    if (!licenseNumber || licenseNumber === '007') return;

    if (userType === 'doctor' && !isValidDoctorPwz(licenseNumber)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Nieprawidłowy numer PWZ',
        path: ['licenseNumber'],
      });
    } else if (
      userType === 'pharmacist' &&
      !isValidPharmacistPwz(licenseNumber)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Nieprawidłowy numer PWZ',
        path: ['licenseNumber'],
      });
    } else if (
      userType === 'nurse_midwife' &&
      !isValidNurseMidwifePwz(licenseNumber)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Nieprawidłowy numer PWZ',
        path: ['licenseNumber'],
      });
    }
  });

// Organization profile schema - address is required
export const OrganizationProfileSchema = z.object({
  userType: z.enum(['pharma', 'medical_place', 'hospital']),
  organizationName: z.string().min(2, 'Nazwa organizacji jest wymagana'),
  phoneNumber: z.string().min(9, 'Numer telefonu jest wymagany'),
  address: z.string().min(5, 'Adres jest wymagany'),
});

export const ProfessionalProfileSchema = z.union([
  IndividualProfileSchema,
  OrganizationProfileSchema,
]);

export type IndividualProfile = z.infer<typeof IndividualProfileSchema>;
export type OrganizationProfile = z.infer<typeof OrganizationProfileSchema>;
export type ProfessionalProfile = z.infer<typeof ProfessionalProfileSchema>;

// Helper to determine if user type is individual
export function isIndividualType(
  type: AccountUserType,
): type is 'doctor' | 'pharmacist' | 'nurse_midwife' {
  return type === 'doctor' || type === 'pharmacist' || type === 'nurse_midwife';
}

// Helper to determine if user type is organization
export function isOrganizationType(
  type: UserType,
): type is 'pharma' | 'medical_place' | 'hospital' {
  return type === 'pharma' || type === 'medical_place' || type === 'hospital';
}
